import { useId, useState } from "react";
import type { ChangeEvent } from "react";
import { Link, matchPath, useLocation, useNavigate } from "react-router-dom";
import type { Me } from "../api/client";
import { useAuth, useMe } from "../auth/AuthContext";
import { t } from "../i18n/t";
import { readLastKommune, type KommuneSelection } from "../lib/storage";
import { routes } from "../lib/routes";

type Section = "dashboard" | "tiltak" | "changes" | "fau" | "exports";

interface Current {
  orgId: string | null;
  nr: string | null;
  section: Section | null;
}

function currentFromPath(pathname: string): Current {
  const k = matchPath("/o/:orgId/k/:nr/*", pathname);
  if (k?.params.orgId && k.params.nr) {
    const rest = k.params["*"] ?? "";
    const section: Section = rest.startsWith("tiltak")
      ? "tiltak"
      : rest.startsWith("endringer")
        ? "changes"
        : rest.startsWith("fau")
          ? "fau"
          : rest.startsWith("eksport")
            ? "exports"
            : "dashboard";
    return { orgId: k.params.orgId, nr: k.params.nr, section };
  }
  const o = matchPath("/o/:orgId/*", pathname);
  return { orgId: o?.params.orgId ?? null, nr: null, section: null };
}

function hasKommune(me: Me, sel: KommuneSelection | null): sel is KommuneSelection {
  return (
    !!sel &&
    me.orgs.some((o) => o.org_id === sel.orgId && o.kommuner.some((k) => k.nummer === sel.nr))
  );
}

/** The kommune the nav links point to: the one in the URL, else the last used, else the first. */
function navTarget(me: Me, current: Current): KommuneSelection | null {
  if (current.orgId && current.nr) return { orgId: current.orgId, nr: current.nr };
  const last = readLastKommune();
  if (hasKommune(me, last) && (!current.orgId || last.orgId === current.orgId)) return last;
  const orgs = current.orgId ? me.orgs.filter((o) => o.org_id === current.orgId) : me.orgs;
  for (const org of orgs) {
    const first = org.kommuner[0];
    if (first) return { orgId: org.org_id, nr: first.nummer };
  }
  return null;
}

function sectionPath(section: Section | null, sel: KommuneSelection): string {
  if (section === "tiltak") return routes.tiltak(sel.orgId, sel.nr);
  if (section === "changes") return routes.changes(sel.orgId, sel.nr);
  if (section === "fau") return routes.fauAdmin(sel.orgId, sel.nr);
  if (section === "exports") return routes.exports(sel.orgId, sel.nr);
  return routes.dashboard(sel.orgId, sel.nr);
}

function KommuneSwitcher({ me, current }: { me: Me; current: Current }) {
  const navigate = useNavigate();
  const id = useId();
  const options = me.orgs.flatMap((o) => o.kommuner.map((k) => ({ org: o, k })));
  if (options.length === 0) return null;
  const value = current.orgId && current.nr ? `${current.orgId}|${current.nr}` : "";
  const multiOrg = me.orgs.length > 1;

  function onChange(e: ChangeEvent<HTMLSelectElement>) {
    const [orgId, nr] = e.target.value.split("|");
    if (orgId && nr) navigate(sectionPath(current.section, { orgId, nr }));
  }

  return (
    <div className="switcher">
      <label htmlFor={id}>{t("nav.switcher")}</label>
      <select id={id} value={value} onChange={onChange}>
        {!value && (
          <option value="" disabled>
            {t("nav.switcherPlaceholder")}
          </option>
        )}
        {me.orgs.map((o) =>
          multiOrg ? (
            <optgroup key={o.org_id} label={o.org_name}>
              {o.kommuner.map((k) => (
                <option key={k.nummer} value={`${o.org_id}|${k.nummer}`}>
                  {k.navn || k.nummer}
                </option>
              ))}
            </optgroup>
          ) : (
            o.kommuner.map((k) => (
              <option key={k.nummer} value={`${o.org_id}|${k.nummer}`}>
                {k.navn || k.nummer}
              </option>
            ))
          ),
        )}
      </select>
    </div>
  );
}

function NavItem({ to, active, children }: { to: string; active: boolean; children: string }) {
  return (
    <li>
      <Link
        to={to}
        className={active ? "active" : undefined}
        aria-current={active ? "page" : undefined}
      >
        {children}
      </Link>
    </li>
  );
}

export function Header() {
  const me = useMe();
  const { logout } = useAuth();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [lastPath, setLastPath] = useState(location.pathname);
  const [loggingOut, setLoggingOut] = useState(false);
  const navId = useId();

  // Close the mobile menu after navigating.
  if (lastPath !== location.pathname) {
    setLastPath(location.pathname);
    setMenuOpen(false);
  }

  const current = currentFromPath(location.pathname);
  const target = navTarget(me, current);
  const membersOrg = current.orgId ?? target?.orgId ?? me.orgs[0]?.org_id ?? null;
  const onMembers = !!matchPath("/o/:orgId/medlemmer", location.pathname);
  const onAdmin = location.pathname.startsWith("/admin");

  async function onLogout() {
    setLoggingOut(true);
    // Clearing the session makes <RequireAuth> send the visitor to the public front page.
    await logout();
  }

  return (
    <header className="app-header">
      <div className="header-bar">
        <Link to={routes.home()} className="brand">
          {t("app.name")}
        </Link>
        <button
          type="button"
          className="btn btn-small menu-toggle"
          aria-expanded={menuOpen}
          aria-controls={navId}
          onClick={() => setMenuOpen((o) => !o)}
        >
          {t("nav.menu")}
        </button>
      </div>
      <div id={navId} className={`header-menu${menuOpen ? " open" : ""}`}>
        <KommuneSwitcher me={me} current={current} />
        <nav aria-label={t("nav.main")}>
          <ul className="nav-list">
            {target && (
              <>
                <NavItem
                  to={routes.dashboard(target.orgId, target.nr)}
                  active={current.section === "dashboard"}
                >
                  {t("nav.overview")}
                </NavItem>
                <NavItem
                  to={routes.tiltak(target.orgId, target.nr)}
                  active={current.section === "tiltak"}
                >
                  {t("nav.tiltak")}
                </NavItem>
                <NavItem
                  to={routes.changes(target.orgId, target.nr)}
                  active={current.section === "changes"}
                >
                  {t("nav.changes")}
                </NavItem>
                <NavItem
                  to={routes.fauAdmin(target.orgId, target.nr)}
                  active={current.section === "fau"}
                >
                  {t("nav.fau")}
                </NavItem>
                <NavItem
                  to={routes.exports(target.orgId, target.nr)}
                  active={current.section === "exports"}
                >
                  {t("nav.exports")}
                </NavItem>
              </>
            )}
            {membersOrg && (
              <NavItem to={routes.members(membersOrg)} active={onMembers}>
                {t("nav.members")}
              </NavItem>
            )}
            {me.user.is_platform_admin && (
              <NavItem to={routes.admin()} active={onAdmin}>
                {t("nav.admin")}
              </NavItem>
            )}
          </ul>
        </nav>
        <div className="user-menu">
          <span className="user-name" title={me.user.email}>
            {t("nav.signedInAs", { name: me.user.name || me.user.email })}
          </span>
          <button
            type="button"
            className="btn btn-small"
            onClick={() => void onLogout()}
            disabled={loggingOut}
          >
            {loggingOut ? t("nav.loggingOut") : t("nav.logout")}
          </button>
        </div>
      </div>
    </header>
  );
}
